import React, { Component } from "react";
import PropTypes from "prop-types";

import GenericChartComponent from "../../GenericChartComponent";
import { getMouseCanvas } from "../../GenericComponent";
import { noop, hexToRGBA } from "../../utils";
import {
  getOrderStyle,
  getOrderSideColor,
  ORDER_UI_COLORS,
} from "../../../../utils/orderStyles.js";
import { formatPrice as formatPriceUtil } from "../../../../utils/priceUtils.js";

// Centralized constants for better maintainability
// Constants for offset calculation
const OFFSET_CONFIG = {
  OVERLAP_THRESHOLD: 20, // 20px proximity threshold (legacy - now calculated dynamically)
  BUTTON_AREA_WIDTH: 220, // Width of button area (trash + gap + quantity x price + send + padding) - increased for larger quantities like 10,000
  SPACING_BETWEEN_LEVELS: 30, // 30px spacing between offset levels
  BUTTON_WIDTH: 30, // Width of individual buttons
  BUTTON_HEIGHT: 20, // Height of buttons
  STOPS_BUTTON_HEIGHT: 16, // Height of stops button (smaller)
  STOPS_BUTTON_OFFSET: 25, // Vertical offset below main buttons
  BASE_PROXIMITY_THRESHOLD: 20, // Base proximity threshold for click detection
  VERTICAL_PADDING: 5, // Additional padding for collision detection
  OFFSET_FROM_EDGE: 58, // Distance from price scale edge (#119)
  LINE_PROXIMITY_THRESHOLD: 12, // 12px above/below = 24px total hit band for full-width line drag
  BUTTON_HOVER_PADDING: 3, // Extra padding around buttons for easier clicks
};

class OrderLine extends Component {
  constructor(props) {
    super(props);

    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
    this.isHover = this.isHover.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleDoubleClick = this.handleDoubleClick.bind(this);

    this.state = {
      hover: false,
    };

    // Cache for last drawn position to ensure hover uses same Y as render
    // This prevents position mismatch when yScale changes between draw and hover
    this.lastDrawnState = null;
  }

  // Performance optimization: Only re-render when order data actually changed
  shouldComponentUpdate(nextProps, nextState) {
    const {
      order: prevOrder,
      selected: prevSelected,
      hovering: prevHovering,
    } = this.props;
    const {
      order: nextOrder,
      selected: nextSelected,
      hovering: nextHovering,
    } = nextProps;

    // Quick checks for common changes
    if (prevSelected !== nextSelected || prevHovering !== nextHovering) {
      return true;
    }
    if (this.state.hover !== nextState.hover) {
      return true;
    }

    // Check order data changes
    return (
      prevOrder.id !== nextOrder.id ||
      prevOrder.price !== nextOrder.price ||
      prevOrder.quantity !== nextOrder.quantity ||
      prevOrder.state !== nextOrder.state ||
      prevOrder.side !== nextOrder.side ||
      prevOrder.isPending !== nextOrder.isPending ||
      prevOrder.oto?.length !== nextOrder.oto?.length
    );
  }

  // Helper method to calculate offset pixels
  getOffsetPixels(y, allOrders, moreProps, orderId) {
    // Calculate the offset using a single consistent algorithm
    return this.calculateDynamicOffsetWithCumulativeSpacing(
      y,
      allOrders,
      moreProps,
      orderId,
    );
  }

  // Unified method that calculates both offset level and cumulative spacing consistently
  calculateDynamicOffsetWithCumulativeSpacing(
    currentY,
    allOrders,
    moreProps,
    currentOrderId,
  ) {
    // Get all orders that would be at the same Y area (vertically overlapping)
    // Use maximum footprint to be conservative since we don't know individual order states here
    const orderFootprint =
      OFFSET_CONFIG.STOPS_BUTTON_OFFSET +
      OFFSET_CONFIG.STOPS_BUTTON_HEIGHT +
      OFFSET_CONFIG.VERTICAL_PADDING;
    const overlappingOrders = allOrders.filter((order) => {
      const orderY = Math.round(moreProps.chartConfig.yScale(order.price));
      const verticalDistance = Math.abs(currentY - orderY);
      return verticalDistance <= orderFootprint;
    });

    // Sort them by priority (open orders first) and then by Y position
    const sortedOrders = [...overlappingOrders].sort((a, b) => {
      const yA = Math.round(moreProps.chartConfig.yScale(a.price));
      const yB = Math.round(moreProps.chartConfig.yScale(b.price));

      const priorityA = a.state === "open" ? 0 : 1;
      const priorityB = b.state === "open" ? 0 : 1;

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      return yA - yB;
    });

    // Calculate offsets and positions for all overlapping orders in one pass
    const orderOffsets = new Map();
    const offsetToOrder = new Map();
    let cumulativeOffsets = new Map(); // Track cumulative pixel offset for each order

    for (let i = 0; i < sortedOrders.length; i++) {
      const order = sortedOrders[i];

      let offset = 0;
      let positionFound = false;

      // Try offset levels until we find one that doesn't conflict
      while (!positionFound && offset < 10) {
        if (!offsetToOrder.has(offset)) {
          positionFound = true;
        } else {
          offset++;
        }
      }

      orderOffsets.set(order.id, offset);
      offsetToOrder.set(offset, order);

      // Calculate cumulative pixel offset for this order
      let cumulativeOffset = 0;
      for (let level = 0; level < offset; level++) {
        const orderAtLevel = offsetToOrder.get(level);
        if (orderAtLevel) {
          const levelWidth = this.getOrderWidth(orderAtLevel.state);
          cumulativeOffset += levelWidth + OFFSET_CONFIG.SPACING_BETWEEN_LEVELS;
        }
      }
      cumulativeOffsets.set(order.id, cumulativeOffset);
    }

    // Return the cumulative pixel offset for the requested order
    return cumulativeOffsets.get(currentOrderId) || 0;
  }

  // Helper method to get the width of an order based on its state
  getOrderWidth(orderState) {
    if (orderState === "open") {
      return OFFSET_CONFIG.BUTTON_WIDTH + 80; // trash button + quantity area
    } else {
      return OFFSET_CONFIG.BUTTON_AREA_WIDTH; // full width for local/pending orders
    }
  }

  // Helper method to check if the stops button should be visible
  shouldShowStopsButton(order) {
    // Only show for local orders
    if (order.state !== "local") {
      return false;
    }

    // Don't show if there are already OTO orders (stops or profits)
    if (order.oto && order.oto.length > 0) {
      return false;
    }

    return true;
  }

  // Helper method to check if the clear stops button should be visible
  shouldShowClearStopsButton(order) {
    // Only show for local orders that have OTO orders
    return order.state === "local" && order.oto && order.oto.length > 0;
  }

  // Helper method to calculate extended proximity threshold that includes stops button area
  getExtendedProximityThreshold(y, order) {
    // Only include stops button area if it should be shown
    if (
      this.shouldShowStopsButton(order) ||
      this.shouldShowClearStopsButton(order)
    ) {
      const stopsButtonBottom =
        y +
        OFFSET_CONFIG.STOPS_BUTTON_OFFSET +
        OFFSET_CONFIG.STOPS_BUTTON_HEIGHT / 2;
      return Math.max(
        OFFSET_CONFIG.BASE_PROXIMITY_THRESHOLD,
        stopsButtonBottom - y + OFFSET_CONFIG.VERTICAL_PADDING,
      );
    }
    // If no stops button, use base threshold
    return OFFSET_CONFIG.BASE_PROXIMITY_THRESHOLD;
  }

  // Helper method to calculate the full vertical footprint of an order including stops button
  getOrderVerticalFootprint(order) {
    // Only include stops button footprint if it should be shown
    if (
      this.shouldShowStopsButton(order) ||
      this.shouldShowClearStopsButton(order)
    ) {
      // Main order line + stops button area + padding
      return (
        OFFSET_CONFIG.STOPS_BUTTON_OFFSET +
        OFFSET_CONFIG.STOPS_BUTTON_HEIGHT +
        OFFSET_CONFIG.VERTICAL_PADDING
      );
    }
    // If no stops button, use smaller footprint
    return (
      OFFSET_CONFIG.BASE_PROXIMITY_THRESHOLD + OFFSET_CONFIG.VERTICAL_PADDING
    );
  }

  handleClick(moreProps) {
    const {
      order,
      onTrashClick,
      onSendClick,
      onStopsClick,
      allOrders = [],
    } = this.props;
    const {
      chartConfig: { width, yScale },
    } = moreProps;
    const {
      mouseXY: [mouseX, mouseY],
    } = moreProps;

    const y = Math.round(yScale(order.price));
    const offsetFromEdge = OFFSET_CONFIG.OFFSET_FROM_EDGE;

    // Calculate dynamic offset based on proximity to other orders
    const offsetPixels = this.getOffsetPixels(
      y,
      allOrders,
      moreProps,
      order.id,
    );

    // Calculate button positions first (needed for both horizontal and vertical checks)
    let trashButtonX, sendButtonX, rightEdgeX;

    if (order.state === "open") {
      // Open orders: align to right edge with smaller area
      const openOrderWidth = OFFSET_CONFIG.BUTTON_WIDTH + 80; // trash button + quantity area
      trashButtonX = width - openOrderWidth - offsetFromEdge - offsetPixels;
      sendButtonX =
        width - OFFSET_CONFIG.BUTTON_WIDTH - 10 - offsetFromEdge - offsetPixels; // Not used for open orders
      rightEdgeX = trashButtonX + openOrderWidth;
    } else {
      // Local/pending orders: use full button area width
      trashButtonX =
        width - OFFSET_CONFIG.BUTTON_AREA_WIDTH - offsetFromEdge - offsetPixels;
      sendButtonX =
        width - OFFSET_CONFIG.BUTTON_WIDTH - 10 - offsetFromEdge - offsetPixels;
      rightEdgeX = sendButtonX + OFFSET_CONFIG.BUTTON_WIDTH;
    }

    // EARLY EXIT: Check if mouse is within horizontal bounds of the button area
    // This prevents capturing clicks from the middle/left of the chart
    if (mouseX < trashButtonX || mouseX > rightEdgeX) {
      return; // Click is horizontally outside our button area, let it propagate
    }

    // Vertical proximity check - only respond if click is close to this order line
    const maxVerticalDistance = this.getExtendedProximityThreshold(y, order);

    if (
      Math.abs(mouseY - y) > maxVerticalDistance &&
      mouseY < y - OFFSET_CONFIG.BASE_PROXIMITY_THRESHOLD
    ) {
      return; // Click is vertically outside our area
    }

    const buttonWidth = OFFSET_CONFIG.BUTTON_WIDTH;
    const buttonHeight = OFFSET_CONFIG.BUTTON_HEIGHT;
    const buttonY = y - 10; // Adjusted for new height

    // Trash button area
    if (
      mouseX >= trashButtonX &&
      mouseX <= trashButtonX + buttonWidth &&
      mouseY >= buttonY &&
      mouseY <= buttonY + buttonHeight
    ) {
      if (onTrashClick) onTrashClick();
      return;
    }

    // Send button area (only for local orders)
    if (
      order.state === "local" &&
      mouseX >= sendButtonX &&
      mouseX <= sendButtonX + buttonWidth &&
      mouseY >= buttonY &&
      mouseY <= buttonY + buttonHeight
    ) {
      if (onSendClick) onSendClick();
      return;
    }

    // Quantity area (between buttons)
    const quantityStartX = trashButtonX + buttonWidth;
    const quantityEndX = sendButtonX;
    if (
      mouseX >= quantityStartX &&
      mouseX <= quantityEndX &&
      mouseY >= buttonY &&
      mouseY <= buttonY + buttonHeight
    ) {
      // Quantity editing now handled by double-click only
      return;
    }

    // "+ Stops" button area (below quantity) - only if button should be visible
    if (this.shouldShowStopsButton(order)) {
      const stopsButtonX = quantityStartX;
      const stopsButtonWidth = quantityEndX - quantityStartX;
      const stopsButtonY =
        y +
        OFFSET_CONFIG.STOPS_BUTTON_OFFSET -
        OFFSET_CONFIG.STOPS_BUTTON_HEIGHT / 2;
      const stopsButtonHeight = OFFSET_CONFIG.STOPS_BUTTON_HEIGHT;

      if (
        mouseX >= stopsButtonX &&
        mouseX <= stopsButtonX + stopsButtonWidth &&
        mouseY >= stopsButtonY &&
        mouseY <= stopsButtonY + stopsButtonHeight
      ) {
        if (onStopsClick) onStopsClick(order.id);
        return;
      }
    }
  }

  handleDoubleClick(moreProps) {
    const { order, onQuantityEdit, allOrders = [] } = this.props;
    const {
      chartConfig: { width, yScale },
    } = moreProps;
    const {
      mouseXY: [mouseX, mouseY],
    } = moreProps;

    const y = Math.round(yScale(order.price));
    const offsetFromEdge = OFFSET_CONFIG.OFFSET_FROM_EDGE;

    // Calculate dynamic offset based on proximity to other orders
    const offsetPixels = this.getOffsetPixels(
      y,
      allOrders,
      moreProps,
      order.id,
    );

    // Calculate button positions first (needed for horizontal bounds check)
    let trashButtonX, rightEdgeX;

    if (order.state === "open") {
      const openOrderWidth = OFFSET_CONFIG.BUTTON_WIDTH + 80;
      trashButtonX = width - openOrderWidth - offsetFromEdge - offsetPixels;
      rightEdgeX = trashButtonX + openOrderWidth;
    } else {
      trashButtonX =
        width - OFFSET_CONFIG.BUTTON_AREA_WIDTH - offsetFromEdge - offsetPixels;
      const sendButtonX =
        width - OFFSET_CONFIG.BUTTON_WIDTH - 10 - offsetFromEdge - offsetPixels;
      rightEdgeX = sendButtonX + OFFSET_CONFIG.BUTTON_WIDTH;
    }

    // EARLY EXIT: Check if mouse is within horizontal bounds of the button area
    if (mouseX < trashButtonX || mouseX > rightEdgeX) {
      return; // Double click is horizontally outside our button area
    }

    // Vertical proximity check
    const maxVerticalDistance = this.getExtendedProximityThreshold(y, order);

    if (
      Math.abs(mouseY - y) > maxVerticalDistance &&
      mouseY < y - OFFSET_CONFIG.BASE_PROXIMITY_THRESHOLD
    ) {
      return;
    }

    const buttonWidth = OFFSET_CONFIG.BUTTON_WIDTH;
    const buttonHeight = OFFSET_CONFIG.BUTTON_HEIGHT;
    const buttonY = y - 10;

    const quantityStartX = trashButtonX + buttonWidth;
    const quantityEndX =
      order.state === "open"
        ? trashButtonX + OFFSET_CONFIG.BUTTON_WIDTH + 80
        : rightEdgeX - buttonWidth;
    if (
      mouseX >= quantityStartX &&
      mouseX <= quantityEndX &&
      mouseY >= buttonY &&
      mouseY <= buttonY + buttonHeight
    ) {
      if (onQuantityEdit) {
        onQuantityEdit(order);
      }
      return;
    }
  }

  isHover(moreProps) {
    // Don't hover main order if OTO drag is in progress
    if (window.oTODragInProgress) {
      return false;
    }

    const { order, allOrders = [] } = this.props;
    const { price } = order;
    const {
      chartConfig: { width, yScale, height },
    } = moreProps;
    const {
      mouseXY: [mouseX, mouseY],
    } = moreProps;

    // FIX: ALWAYS recalculate Y from the current yScale to ensure consistency
    // The cache was causing synchronization issues during Y-axis zoom/pan operations
    // where the cached Y position didn't match the current scale
    const y = Math.round(yScale(price));

    if (y >= 0 && y <= height) {
      // Only make the button+quantity area draggable with proper offset
      const offsetFromEdge = OFFSET_CONFIG.OFFSET_FROM_EDGE;

      // Calculate dynamic offset for this specific order
      const offsetPixels = this.getOffsetPixels(
        y,
        allOrders,
        moreProps,
        order.id,
      );

      // Define draggable area: button area with offset applied - use same positioning as drawButtonArea
      let leftX, rightX, trashButtonX, sendButtonX;

      if (order.state === "open") {
        // Open orders: align to right edge with smaller area
        const openOrderWidth = OFFSET_CONFIG.BUTTON_WIDTH + 80; // trash button + quantity area
        trashButtonX = width - openOrderWidth - offsetFromEdge - offsetPixels;
        leftX = trashButtonX; // Start of trash button
        rightX = leftX + openOrderWidth; // End of quantity area
        sendButtonX = rightX; // No send button for open orders
      } else {
        // Local/pending orders: use full button area width
        trashButtonX =
          width -
          OFFSET_CONFIG.BUTTON_AREA_WIDTH -
          offsetFromEdge -
          offsetPixels;
        sendButtonX =
          width -
          OFFSET_CONFIG.BUTTON_WIDTH -
          10 -
          offsetFromEdge -
          offsetPixels;
        leftX = trashButtonX; // Start of trash button
        rightX = sendButtonX + OFFSET_CONFIG.BUTTON_WIDTH; // End of send button
      }

      // --- Zone 1: Full horizontal line drag (skip OCO orders which don't draw a line) ---
      if (
        !order.oco &&
        mouseX < leftX &&
        Math.abs(mouseY - y) <= OFFSET_CONFIG.LINE_PROXIMITY_THRESHOLD
      ) {
        // Nearest-line priority: if another order line is closer to mouseY, yield to it
        let isNearest = true;
        for (let i = 0; i < allOrders.length; i++) {
          const other = allOrders[i];
          if (other.id === order.id || other.oco) continue;
          const otherY = Math.round(yScale(other.price));
          if (
            otherY >= 0 &&
            otherY <= height &&
            Math.abs(mouseY - otherY) < Math.abs(mouseY - y)
          ) {
            isNearest = false;
            break;
          }
        }
        if (isNearest) {
          return true;
        }
      }

      // --- Zone 2: Button area (existing logic) ---
      // Include vertical area for stops button
      const hoverHeight = this.getExtendedProximityThreshold(y, order);

      // Check if mouse is in the general button area
      const inButtonArea =
        mouseX >= leftX &&
        mouseX <= rightX &&
        Math.abs(mouseY - y) < hoverHeight;

      if (!inButtonArea) {
        return false;
      }

      // EXCLUDE trash button area from drag detection (allow click only)
      const buttonHeight = OFFSET_CONFIG.BUTTON_HEIGHT;
      const buttonY = y - 10;
      const inTrashButton =
        mouseX >= trashButtonX &&
        mouseX <= trashButtonX + OFFSET_CONFIG.BUTTON_WIDTH &&
        mouseY >= buttonY &&
        mouseY <= buttonY + buttonHeight;

      if (inTrashButton) {
        return false; // Don't allow drag on trash button
      }

      // EXCLUDE send button area from drag detection (for local orders)
      if (order.state === "local") {
        const inSendButton =
          mouseX >= sendButtonX &&
          mouseX <= sendButtonX + OFFSET_CONFIG.BUTTON_WIDTH &&
          mouseY >= buttonY &&
          mouseY <= buttonY + buttonHeight;

        if (inSendButton) {
          return false; // Don't allow drag on send button
        }
      }

      return true;
    }
    return false;
  }

  drawOnCanvas(ctx, moreProps) {
    const { order, selected, hovering } = this.props;
    const { state, side, price, isPending } = order;

    const {
      chartConfig: { width, yScale },
    } = moreProps;
    const y = Math.round(yScale(price));

    // Cache drawn position (kept for potential future use, but not used in isHover anymore)
    this.lastDrawnState = {
      y,
      price: order.price,
      orderId: order.id,
      yScaleDomain: yScale.domain().join(","),
    };

    // Get styling from centralized orderStyles.js
    const orderStyle = getOrderStyle(side, state);
    let stroke = orderStyle.stroke;
    const opacity = orderStyle.opacity;
    let strokeDasharray = orderStyle.strokeDasharray;

    // Visual indicator for pending modification - amber color and thicker line
    if (isPending) {
      stroke = "#F59E0B"; // Amber/yellow color for pending state
    }

    // Convert strokeDasharray to canvas format
    if (strokeDasharray === "solid" || strokeDasharray === "none") {
      strokeDasharray = [];
    } else if (strokeDasharray === "ShortDash2") {
      strokeDasharray = [5, 5];
    } else if (strokeDasharray === "ShortDash") {
      strokeDasharray = [3, 3];
    } else if (strokeDasharray === "Dot") {
      strokeDasharray = [1, 2];
    } else if (
      typeof strokeDasharray === "string" &&
      strokeDasharray.includes(",")
    ) {
      // Handle patterns like '1,8', '2,2', '1,1' from orderStyles.js
      strokeDasharray = strokeDasharray
        .split(",")
        .map((n) => parseInt(n.trim()));
    } else {
      strokeDasharray = [];
    }

    // Apply selection/hover effects - when highlighted, always use full opacity
    // For local orders, respect the exact opacity from orderStyles without reduction
    const finalOpacity =
      selected || hovering ? 1.0 : state === "local" ? opacity : opacity * 0.8;
    // Use thicker line for pending orders
    const strokeWidth = isPending ? 2.5 : selected || hovering ? 2 : 1;

    // Draw horizontal line across the chart (full width).
    // #397: pernas OCO (Gain/Loss pós-fill, order.oco setado) TAMBÉM desenham a
    // linha — o skip original (618474bd, junto com o OCOConnectorLine) deixava os
    // pills sem referência de preço nos candles. O hover/drag da linha full-width
    // segue desabilitado para OCO (ver isHover Zone 1) — mudança puramente visual.
    ctx.strokeStyle = hexToRGBA(stroke, finalOpacity);
    ctx.lineWidth = strokeWidth;
    ctx.beginPath();
    ctx.setLineDash(strokeDasharray);
    ctx.moveTo(0, y);
    ctx.lineTo(width, y); // Full width of chart
    ctx.stroke();
    ctx.setLineDash([]);

    // Draw unified button background and buttons
    this.drawButtonArea(ctx, width, y, side, state, order, moreProps);
  }

  drawButtonArea(ctx, width, y, side, state, order, moreProps) {
    const offsetFromEdge = OFFSET_CONFIG.OFFSET_FROM_EDGE;
    const { allOrders = [] } = this.props;

    // Calculate dynamic offset based on proximity to other orders
    const offsetPixels = this.getOffsetPixels(
      y,
      allOrders,
      moreProps,
      order.id,
    );

    // Get order style for consistent opacity
    const orderStyle = getOrderStyle(side, state);
    // For local orders, respect the exact opacity from orderStyles without reduction
    const finalOpacity =
      this.props.selected || this.props.hovering
        ? 1.0
        : state === "local"
          ? orderStyle.opacity
          : orderStyle.opacity * 0.8;

    // Button positions - different logic for open orders vs local/pending
    let trashButtonX, sendButtonX, backgroundX, backgroundWidth;

    if (state === "open") {
      // Open orders: align to right edge with smaller area (trash + quantity only)
      const openOrderWidth = OFFSET_CONFIG.BUTTON_WIDTH + 80; // trash button + quantity area
      trashButtonX = width - openOrderWidth - offsetFromEdge - offsetPixels;
      sendButtonX =
        width - OFFSET_CONFIG.BUTTON_WIDTH - 10 - offsetFromEdge - offsetPixels; // Not used but kept for consistency
      backgroundX = trashButtonX - 2;
      backgroundWidth = openOrderWidth + 4; // Add padding
    } else {
      // Local/pending orders: use full button area width
      trashButtonX =
        width - OFFSET_CONFIG.BUTTON_AREA_WIDTH - offsetFromEdge - offsetPixels;
      sendButtonX =
        width - OFFSET_CONFIG.BUTTON_WIDTH - 10 - offsetFromEdge - offsetPixels; // 10px gap from edge
      const sendButtonWidth = OFFSET_CONFIG.BUTTON_WIDTH;
      const sendButtonRightEdge = sendButtonX + sendButtonWidth;
      backgroundX = trashButtonX - 2;
      backgroundWidth = sendButtonRightEdge - backgroundX + 2;
    }

    const backgroundHeight = 24; // Increased to accommodate taller buttons (20px + padding)
    const backgroundY = y - 12; // Adjusted for taller background

    // Draw appropriate background based on order state
    if (state === "local") {
      // Save current context state
      ctx.save();

      // Apply blur effect from orderStyles
      const blurAmount = orderStyle.blur || 0;
      if (blurAmount > 0) {
        ctx.filter = `blur(${blurAmount}px)`;
      }

      // Draw blurred background with side-specific color (like pending orders)
      const sideColor = getOrderSideColor(side);
      // For local orders, use nearly full opacity from orderStyle; for others use low opacity
      const bgOpacity = state === "local" ? finalOpacity * 0.95 : 0.3; // 95% of final opacity for strong visibility
      const borderOpacity = state === "local" ? finalOpacity : 0.4; // Full opacity for border
      const backgroundColor = hexToRGBA(sideColor, bgOpacity);
      const borderColor = hexToRGBA(orderStyle.stroke, borderOpacity);

      this.roundRect(
        ctx,
        backgroundX,
        backgroundY,
        backgroundWidth,
        backgroundHeight,
        6,
      );
      ctx.fillStyle = backgroundColor;
      ctx.fill();
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = 1;
      ctx.stroke();

      // Restore context to remove blur for buttons and text
      ctx.restore();

      // Draw trash button with opacity (only for local orders) - no blur
      this.drawTrashButton(ctx, trashButtonX, y, finalOpacity);

      // Draw send button with opacity (only for local orders) - no blur
      this.drawSendButton(ctx, sendButtonX, y, finalOpacity);

      // Draw quantity with opacity (only for local orders) - no blur
      this.drawQuantity(
        ctx,
        trashButtonX + 30,
        sendButtonX,
        y,
        order.quantity,
        order.price,
        finalOpacity,
      );

      // Draw "+ Stops" button below quantity area (only if conditions are met)
      if (this.shouldShowStopsButton(order)) {
        const quantityStartX = trashButtonX + 30;
        const quantityWidth = sendButtonX - quantityStartX;
        this.drawStopsButton(
          ctx,
          quantityStartX,
          y,
          quantityWidth,
          side,
          finalOpacity,
        );
      }
    } else if (state === "pending") {
      // Save current context state
      ctx.save();

      // Apply blur effect from orderStyles (simple approach)
      const blurAmount = orderStyle.blur || 0;
      if (blurAmount > 0) {
        ctx.filter = `blur(${blurAmount}px)`;
      }

      // Use order side color for pending background (respects orderStyles opacity)
      const sideColor = getOrderSideColor(side);
      const backgroundColor = hexToRGBA(sideColor, orderStyle.opacity || 0.3);
      const borderColor = hexToRGBA(orderStyle.stroke, 0.4);

      // Use the same background dimensions as local orders
      const labelX = backgroundX;
      const labelY = backgroundY;
      const labelWidth = backgroundWidth;
      const labelHeight = backgroundHeight;

      this.roundRect(ctx, labelX, labelY, labelWidth, labelHeight, 6);
      ctx.fillStyle = backgroundColor;
      ctx.fill();

      // Restore context to remove blur for border and text
      ctx.restore();

      // Draw crisp border (after removing blur)
      this.roundRect(ctx, labelX, labelY, labelWidth, labelHeight, 6);
      ctx.strokeStyle = borderColor;
      ctx.lineWidth = 1;
      ctx.stroke();

      // Draw "Enviando..." label (static opacity)
      this.drawPendenteLabel(ctx, labelX, labelWidth, y, finalOpacity);
    } else if (state === "open") {
      // Open orders should show trash and quantity like local orders, but no send button
      // Save current context state
      ctx.save();

      // Apply blur effect from orderStyles
      const blurAmount = orderStyle.blur || 0;
      if (blurAmount > 0) {
        ctx.filter = `blur(${blurAmount}px)`;
      }

      // Draw blurred background with side-specific color
      const sideColor = getOrderSideColor(side);
      const bgOpacity = finalOpacity * 0.95;
      const borderOpacity = finalOpacity;
      const backgroundColor = hexToRGBA(sideColor, bgOpacity);
      // Use amber border when pending modification
      const borderColor = order.isPending
        ? hexToRGBA("#F59E0B", 1.0)
        : hexToRGBA(orderStyle.stroke, borderOpacity);

      // Use the calculated background dimensions (aligned to right)
      this.roundRect(
        ctx,
        backgroundX,
        backgroundY,
        backgroundWidth,
        backgroundHeight,
        6,
      );
      ctx.fillStyle = backgroundColor;
      ctx.fill();
      ctx.strokeStyle = borderColor;
      // Use thicker border when pending
      ctx.lineWidth = order.isPending ? 2 : 1;
      ctx.stroke();

      // Restore context to remove blur for buttons and text
      ctx.restore();

      // Draw trash button for open orders (they can be cancelled)
      this.drawTrashButton(ctx, trashButtonX, y, finalOpacity);

      // Draw quantity for open orders with smaller area (no send button space)
      const quantityEndX = trashButtonX + OFFSET_CONFIG.BUTTON_WIDTH + 80; // Smaller quantity area
      this.drawQuantity(
        ctx,
        trashButtonX + 30,
        quantityEndX,
        y,
        order.quantity,
        order.price,
        finalOpacity,
      );
    }
  }

  drawTrashButton(ctx, x, y, opacity = 1.0) {
    const buttonWidth = OFFSET_CONFIG.BUTTON_WIDTH;
    const buttonHeight = OFFSET_CONFIG.BUTTON_HEIGHT;
    const buttonY = y - 10; // Updated to match click detection

    // Button background with opacity
    const backgroundColor = hexToRGBA(ORDER_UI_COLORS.trashButton, opacity);
    this.roundRect(ctx, x, buttonY, buttonWidth, buttonHeight, 3);
    ctx.fillStyle = backgroundColor;
    ctx.fill();

    // Trash icon (X symbol) with enhanced visibility
    ctx.fillStyle = hexToRGBA(
      ORDER_UI_COLORS.textPrimary,
      Math.max(opacity, 0.9),
    ); // Ensure minimum visibility
    ctx.font =
      "12px SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    // Add text shadow for better visibility
    ctx.shadowColor = ORDER_UI_COLORS.textShadow;
    ctx.shadowBlur = 2;
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;

    ctx.fillText("×", x + buttonWidth / 2, buttonY + buttonHeight / 2);

    // Reset shadow
    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
  }

  drawSendButton(ctx, x, y, opacity = 1.0) {
    const buttonWidth = OFFSET_CONFIG.BUTTON_WIDTH;
    const buttonHeight = OFFSET_CONFIG.BUTTON_HEIGHT;
    const buttonY = y - 10; // Updated to match click detection

    // Button background with opacity
    const backgroundColor = hexToRGBA(ORDER_UI_COLORS.sendButton, opacity);
    this.roundRect(ctx, x, buttonY, buttonWidth, buttonHeight, 3);
    ctx.fillStyle = backgroundColor;
    ctx.fill();

    // Send icon (right arrow) with enhanced visibility
    ctx.fillStyle = hexToRGBA(
      ORDER_UI_COLORS.textPrimary,
      Math.max(opacity, 0.9),
    ); // Ensure minimum visibility
    ctx.font =
      "12px SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    // Add text shadow for better visibility
    ctx.shadowColor = ORDER_UI_COLORS.textShadow;
    ctx.shadowBlur = 2;
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;

    ctx.fillText("→", x + buttonWidth / 2, buttonY + buttonHeight / 2);

    // Reset shadow
    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
  }

  // Helper method to format price using centralized price utility
  formatPrice(price, symbol) {
    try {
      return formatPriceUtil(symbol, price);
    } catch {
      // Fallback to 2 decimal places if anything fails
      return Number(price).toFixed(2);
    }
  }

  drawQuantity(ctx, startX, endX, y, quantity, price, opacity = 1.0) {
    // FIX #138: Guard against invalid price display (belt and suspenders)
    if (!price || price <= 0 || !isFinite(price)) {
      console.warn("⚠️ OrderLine.drawQuantity: Skipping invalid price:", price);
      return;
    }

    const centerX = (startX + endX) / 2;

    // Get symbol from order props
    const { order } = this.props;
    const symbol = order ? order.symbol : null;

    // Format price using the same logic as PriceCoordinate
    const formattedPrice = this.formatPrice(price, symbol);

    // Get order type and styling
    const orderType = order?.orderType;
    const orderSide = order?.side;
    const hasOrderType =
      orderType &&
      (orderType === "LIMIT" ||
        orderType === "STOPLIMIT" ||
        orderType === "MARKET");

    // For open orders, use simplified display without badges to save space
    if (order?.state === "open") {
      const quantityText = `${quantity} × ${formattedPrice}`;

      ctx.fillStyle = hexToRGBA(
        ORDER_UI_COLORS.textPrimary,
        Math.max(opacity, 0.9),
      );
      ctx.font =
        "600 10px 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      // Subtle text shadow
      ctx.shadowColor = ORDER_UI_COLORS.textShadow;
      ctx.shadowBlur = 1;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 1;

      ctx.fillText(quantityText, centerX, y);
    } else if (hasOrderType) {
      // Professional badge design for order types (local and pending orders)
      const isLimit = orderType === "LIMIT";
      const isStopLimit = orderType === "STOPLIMIT";
      const isMarket = orderType === "MARKET";

      let badgeConfig, badgeText;
      if (isLimit) {
        badgeConfig =
          ORDER_UI_COLORS.orderTypeBadge.limit[orderSide] ||
          ORDER_UI_COLORS.orderTypeBadge.limit.buy;
        badgeText = "LMT";
      } else if (isStopLimit) {
        badgeConfig =
          ORDER_UI_COLORS.orderTypeBadge.stopLimit[orderSide] ||
          ORDER_UI_COLORS.orderTypeBadge.stopLimit.buy;
        badgeText = "STP";
      } else if (isMarket) {
        badgeConfig = ORDER_UI_COLORS.orderTypeBadge.market;
        badgeText = "MKT";
      }

      const quantityText = `${quantity} × ${formattedPrice}`;

      // Calculate badge dimensions
      const badgeWidth = 32;
      const badgeHeight = 16;
      const badgeX = startX + 6;
      const badgeY = y - 8;
      const textGap = 8;

      // Draw professional badge
      ctx.save();

      // Badge background with rounded corners
      ctx.fillStyle = hexToRGBA(badgeConfig.background, Math.max(opacity, 0.9));
      ctx.strokeStyle = hexToRGBA(badgeConfig.border, Math.max(opacity, 0.9));
      ctx.lineWidth = 1;

      this.roundRect(ctx, badgeX, badgeY, badgeWidth, badgeHeight, 3);
      ctx.fill();
      ctx.stroke();

      // Badge shadow for depth
      ctx.shadowColor = badgeConfig.shadow;
      ctx.shadowBlur = 3;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 1;

      // Badge text
      ctx.fillStyle = hexToRGBA(badgeConfig.text, Math.max(opacity, 0.95));
      ctx.font =
        "700 9px 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.shadowColor = "transparent";
      ctx.shadowBlur = 0;

      ctx.fillText(
        badgeText,
        badgeX + badgeWidth / 2,
        badgeY + badgeHeight / 2,
      );

      ctx.restore();

      // Quantity text positioned after badge
      const quantityStartX = badgeX + badgeWidth + textGap;
      const quantityEndX = endX - 8;
      const quantityCenterX = (quantityStartX + quantityEndX) / 2;

      ctx.fillStyle = hexToRGBA(
        ORDER_UI_COLORS.textPrimary,
        Math.max(opacity, 0.9),
      );
      ctx.font =
        "600 10px 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      // Subtle text shadow
      ctx.shadowColor = ORDER_UI_COLORS.textShadow;
      ctx.shadowBlur = 1;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 1;

      ctx.fillText(quantityText, quantityCenterX, y);
    } else {
      // Fallback for orders without type or unknown types
      const quantityText = `${quantity} × ${formattedPrice}`;

      ctx.fillStyle = hexToRGBA(
        ORDER_UI_COLORS.textPrimary,
        Math.max(opacity, 0.9),
      );
      ctx.font =
        "600 10px 'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";

      ctx.shadowColor = ORDER_UI_COLORS.textShadow;
      ctx.shadowBlur = 1;
      ctx.shadowOffsetX = 0;
      ctx.shadowOffsetY = 1;

      ctx.fillText(quantityText, centerX, y);
    }

    // Reset shadow
    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
  }

  drawPendenteLabel(ctx, labelX, labelWidth, y, opacity = 1.0) {
    const centerX = labelX + labelWidth / 2;

    // Draw "Pendente" text with high contrast color
    ctx.fillStyle = hexToRGBA(
      ORDER_UI_COLORS.textPrimary,
      Math.max(opacity, 0.9),
    ); // Ensure visibility
    ctx.font =
      "700 11px SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    // Add text shadow for better visibility
    ctx.shadowColor = ORDER_UI_COLORS.textShadow;
    ctx.shadowBlur = 2;
    ctx.shadowOffsetX = 1;
    ctx.shadowOffsetY = 1;

    ctx.fillText("Enviando...", centerX, y);

    // Reset shadow
    ctx.shadowColor = "transparent";
    ctx.shadowBlur = 0;
    ctx.shadowOffsetX = 0;
    ctx.shadowOffsetY = 0;
  }

  // Helper method to draw rounded rectangles
  roundRect(ctx, x, y, width, height, radius) {
    ctx.beginPath();
    ctx.moveTo(x + radius, y);
    ctx.lineTo(x + width - radius, y);
    ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
    ctx.lineTo(x + width, y + height - radius);
    ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    ctx.lineTo(x + radius, y + height);
    ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
    ctx.lineTo(x, y + radius);
    ctx.quadraticCurveTo(x, y, x + radius, y);
    ctx.closePath();
  }

  // Draw "+ Stops" button below quantity area
  drawStopsButton(ctx, x, y, width, side, opacity = 1.0) {
    const buttonY =
      y +
      OFFSET_CONFIG.STOPS_BUTTON_OFFSET -
      OFFSET_CONFIG.STOPS_BUTTON_HEIGHT / 2;
    const buttonHeight = OFFSET_CONFIG.STOPS_BUTTON_HEIGHT;

    // Use opposite side color: buy orders get red stops, sell orders get blue stops
    const oppositeSideColor =
      side === "buy" ? getOrderSideColor("sell") : getOrderSideColor("buy");

    // Button background with subtle styling
    const backgroundColor = hexToRGBA(oppositeSideColor, opacity * 0.7);
    this.roundRect(ctx, x, buttonY, width, buttonHeight, 2);
    ctx.fillStyle = backgroundColor;
    ctx.fill();

    // Button border
    ctx.strokeStyle = hexToRGBA(oppositeSideColor, opacity);
    ctx.lineWidth = 1;
    ctx.stroke();

    // Button text
    ctx.fillStyle = hexToRGBA("#FFFFFF", opacity);
    ctx.font =
      "600 10px SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    ctx.fillText("+ Stops", x + width / 2, buttonY + buttonHeight / 2);
  }

  // Helper method to calculate dynamic offset based on proximity to other orders
  calculateDynamicOffset(currentY, allOrders, moreProps, currentOrderId) {
    // Sort orders with priority: open orders first (closest to right edge), then local/pending
    // Within each group, sort by Y position (price)
    const sortedOrders = [...allOrders].sort((a, b) => {
      const yA = Math.round(moreProps.chartConfig.yScale(a.price));
      const yB = Math.round(moreProps.chartConfig.yScale(b.price));

      // Priority order: open orders first, then local/pending orders
      const priorityA = a.state === "open" ? 0 : 1;
      const priorityB = b.state === "open" ? 0 : 1;

      // First sort by priority (open orders first)
      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      // Within same priority group, sort by Y position (top to bottom)
      return yA - yB;
    });

    // Calculate offsets for all orders iteratively to avoid recursion
    const orderOffsets = new Map();

    for (let i = 0; i < sortedOrders.length; i++) {
      const currentOrder = sortedOrders[i];
      const orderY = Math.round(
        moreProps.chartConfig.yScale(currentOrder.price),
      );

      let offset = 0;
      let positionFound = false;

      // Try offset levels until we find one that doesn't conflict
      while (!positionFound && offset < 10) {
        // Safety limit
        let conflictsWithPrevious = false;

        // Check this offset against all previous orders in same Y area
        for (let j = 0; j < i; j++) {
          const prevOrder = sortedOrders[j];
          const prevY = Math.round(
            moreProps.chartConfig.yScale(prevOrder.price),
          );
          const prevOffset = orderOffsets.get(prevOrder.id) || 0;

          // Calculate full vertical footprint collision detection
          // Use maximum footprint to be conservative
          const orderFootprint =
            OFFSET_CONFIG.STOPS_BUTTON_OFFSET +
            OFFSET_CONFIG.STOPS_BUTTON_HEIGHT +
            OFFSET_CONFIG.VERTICAL_PADDING;
          const verticalDistance = Math.abs(orderY - prevY);

          // If orders' vertical footprints overlap and use same offset level
          if (verticalDistance <= orderFootprint && offset === prevOffset) {
            conflictsWithPrevious = true;
            break;
          }
        }

        if (!conflictsWithPrevious) {
          positionFound = true;
        } else {
          offset++;
        }
      }

      orderOffsets.set(currentOrder.id, offset);
    }

    // Return the offset for the requested order
    return orderOffsets.get(currentOrderId) || 0;
  }

  getQuantityInputX() {
    const { chartConfig } = this.context;
    if (!chartConfig) return 0;
    const { width } = chartConfig;
    const offsetFromEdge = OFFSET_CONFIG.OFFSET_FROM_EDGE + 12; // Base offset + small adjustment for input
    return width - OFFSET_CONFIG.BUTTON_AREA_WIDTH - offsetFromEdge + 30; // Position between trash and send buttons
  }

  getQuantityInputY() {
    const { order } = this.props;
    const { chartConfig } = this.context;
    if (!chartConfig || !order) return 0;
    const { yScale } = chartConfig;
    return yScale(order.price) - 10; // Center on the order line
  }

  renderSVG() {
    throw new Error("SVG rendering not implemented for OrderLine");
  }

  // Check if mouse is hovering over the trash button (for pointer cursor)
  isHoverTrashButton(moreProps) {
    const { order, allOrders = [] } = this.props;
    const { price } = order;
    const {
      chartConfig: { width, yScale, height },
    } = moreProps;
    const {
      mouseXY: [mouseX, mouseY],
    } = moreProps;

    const y = Math.round(yScale(price));

    if (y < 0 || y > height) {
      return false;
    }

    const offsetFromEdge = OFFSET_CONFIG.OFFSET_FROM_EDGE;
    const offsetPixels = this.getOffsetPixels(
      y,
      allOrders,
      moreProps,
      order.id,
    );

    let trashButtonX;
    if (order.state === "open") {
      const openOrderWidth = OFFSET_CONFIG.BUTTON_WIDTH + 80;
      trashButtonX = width - openOrderWidth - offsetFromEdge - offsetPixels;
    } else {
      trashButtonX =
        width - OFFSET_CONFIG.BUTTON_AREA_WIDTH - offsetFromEdge - offsetPixels;
    }

    const buttonHeight = OFFSET_CONFIG.BUTTON_HEIGHT;
    const buttonY = y - 10;
    const bp = OFFSET_CONFIG.BUTTON_HOVER_PADDING;

    return (
      mouseX >= trashButtonX - bp &&
      mouseX <= trashButtonX + OFFSET_CONFIG.BUTTON_WIDTH + bp &&
      mouseY >= buttonY - bp &&
      mouseY <= buttonY + buttonHeight + bp
    );
  }

  // Check if mouse is hovering over the send button (for pointer cursor)
  isHoverSendButton(moreProps) {
    const { order, allOrders = [] } = this.props;

    // Send button only exists for local orders
    if (order.state !== "local") {
      return false;
    }

    const { price } = order;
    const {
      chartConfig: { width, yScale, height },
    } = moreProps;
    const {
      mouseXY: [mouseX, mouseY],
    } = moreProps;

    const y = Math.round(yScale(price));

    if (y < 0 || y > height) {
      return false;
    }

    const offsetFromEdge = OFFSET_CONFIG.OFFSET_FROM_EDGE;
    const offsetPixels = this.getOffsetPixels(
      y,
      allOrders,
      moreProps,
      order.id,
    );

    const sendButtonX =
      width - OFFSET_CONFIG.BUTTON_WIDTH - 10 - offsetFromEdge - offsetPixels;
    const buttonHeight = OFFSET_CONFIG.BUTTON_HEIGHT;
    const buttonY = y - 10;
    const bp = OFFSET_CONFIG.BUTTON_HOVER_PADDING;

    return (
      mouseX >= sendButtonX - bp &&
      mouseX <= sendButtonX + OFFSET_CONFIG.BUTTON_WIDTH + bp &&
      mouseY >= buttonY - bp &&
      mouseY <= buttonY + buttonHeight + bp
    );
  }

  // Check if mouse is hovering over the stops button (for pointer cursor)
  isHoverStopsButton(moreProps) {
    const { order, allOrders = [] } = this.props;

    // Stops button only exists for local orders without OTO
    if (!this.shouldShowStopsButton(order)) {
      return false;
    }

    const { price } = order;
    const {
      chartConfig: { width, yScale, height },
    } = moreProps;
    const {
      mouseXY: [mouseX, mouseY],
    } = moreProps;

    const y = Math.round(yScale(price));

    if (y < 0 || y > height) {
      return false;
    }

    const offsetFromEdge = OFFSET_CONFIG.OFFSET_FROM_EDGE;
    const offsetPixels = this.getOffsetPixels(
      y,
      allOrders,
      moreProps,
      order.id,
    );

    const trashButtonX =
      width - OFFSET_CONFIG.BUTTON_AREA_WIDTH - offsetFromEdge - offsetPixels;
    const sendButtonX =
      width - OFFSET_CONFIG.BUTTON_WIDTH - 10 - offsetFromEdge - offsetPixels;

    const quantityStartX = trashButtonX + OFFSET_CONFIG.BUTTON_WIDTH;
    const stopsButtonX = quantityStartX;
    const stopsButtonWidth = sendButtonX - quantityStartX;
    const stopsButtonY =
      y +
      OFFSET_CONFIG.STOPS_BUTTON_OFFSET -
      OFFSET_CONFIG.STOPS_BUTTON_HEIGHT / 2;
    const stopsButtonHeight = OFFSET_CONFIG.STOPS_BUTTON_HEIGHT;

    return (
      mouseX >= stopsButtonX &&
      mouseX <= stopsButtonX + stopsButtonWidth &&
      mouseY >= stopsButtonY &&
      mouseY <= stopsButtonY + stopsButtonHeight
    );
  }

  render() {
    const { order, onDragStart, onDrag, onDragComplete, onHover, onUnHover } =
      this.props;

    // Only draggable for local and open orders
    const isDraggable = order.state === "local" || order.state === "open";

    // Show buttons for local and open orders
    const showTrashButton = order.state === "local" || order.state === "open";
    const showSendButton = order.state === "local";
    const showStopsButton = this.shouldShowStopsButton(order);

    return (
      <g>
        {/* Main draggable component with ns-resize cursor */}
        <GenericChartComponent
          key="drag"
          clip={true}
          isHover={this.isHover}
          svgDraw={this.renderSVG}
          canvasToDraw={getMouseCanvas}
          canvasDraw={this.drawOnCanvas}
          selected={isDraggable}
          interactiveCursorClass={
            isDraggable ? "react-stockcharts-ns-resize-cursor" : undefined
          }
          enableDragOnHover
          onDragStart={onDragStart}
          onDrag={onDrag}
          onDragComplete={onDragComplete}
          onHover={onHover}
          onUnHover={onUnHover}
          onClick={this.handleClick}
          onDoubleClick={this.handleDoubleClick}
          drawOn={[
            "mousemove",
            "mouseleave",
            "pan",
            "drag",
            "draw",
            "zoom",
            "xAxisZoom",
            "yAxisReset",
            "yAxisZoomCommit",
            "resetYDomain",
            "resize",
          ]}
          data-evo-id="interactive-comp-order-line"
        />
        {/* Trash button overlay with pointer cursor - NO onClick, just cursor */}
        {showTrashButton && (
          <GenericChartComponent
            key="trash"
            canvasToDraw={getMouseCanvas}
            canvasDraw={() => {}}
            drawOn={["mousemove"]}
            isHover={this.isHoverTrashButton.bind(this)}
            selected={true}
            interactiveCursorClass="react-stockcharts-pointer-cursor"
          />
        )}
        {/* Send button overlay with pointer cursor - NO onClick, just cursor */}
        {showSendButton && (
          <GenericChartComponent
            key="send"
            canvasToDraw={getMouseCanvas}
            canvasDraw={() => {}}
            drawOn={["mousemove"]}
            isHover={this.isHoverSendButton.bind(this)}
            selected={true}
            interactiveCursorClass="react-stockcharts-pointer-cursor"
          />
        )}
        {/* Stops button overlay with pointer cursor */}
        {showStopsButton && (
          <GenericChartComponent
            key="stops"
            canvasToDraw={getMouseCanvas}
            canvasDraw={() => {}}
            drawOn={["mousemove"]}
            isHover={this.isHoverStopsButton.bind(this)}
            selected={true}
            interactiveCursorClass="react-stockcharts-pointer-cursor"
          />
        )}
      </g>
    );
  }
}

OrderLine.propTypes = {
  order: PropTypes.shape({
    id: PropTypes.string.isRequired,
    state: PropTypes.oneOf([
      "local",
      "pending",
      "open",
      "filled",
      "cancelled",
      "rejected",
    ]).isRequired,
    side: PropTypes.oneOf(["buy", "sell"]).isRequired,
    price: PropTypes.number.isRequired,
    quantity: PropTypes.number.isRequired,
    symbol: PropTypes.string.isRequired,
    orderType: PropTypes.oneOf(["MARKET", "LIMIT", "STOPLIMIT"]),
    oto: PropTypes.array, // Array of OTO (One-Triggers-Other) orders
    oco: PropTypes.string, // OCO (One-Cancels-Other) order ID
  }).isRequired,

  onDragStart: PropTypes.func,
  onDrag: PropTypes.func,
  onDragComplete: PropTypes.func,
  onHover: PropTypes.func,
  onUnHover: PropTypes.func,
  onTrashClick: PropTypes.func,
  onSendClick: PropTypes.func,
  onStopsClick: PropTypes.func,
  onQuantityClick: PropTypes.func,
  onQuantityEdit: PropTypes.func,
  onDoubleClick: PropTypes.func,

  selected: PropTypes.bool,
  hovering: PropTypes.bool,
  offset: PropTypes.number,
  allOrders: PropTypes.array,
};

OrderLine.defaultProps = {
  onDragStart: noop,
  onDrag: noop,
  onDragComplete: noop,
  onHover: noop,
  onUnHover: noop,
  onTrashClick: noop,
  onSendClick: noop,
  onStopsClick: noop,
  onQuantityClick: noop,
  onQuantityEdit: noop,
  onDoubleClick: noop,
  selected: false,
  hovering: false,
  offset: 0,
  allOrders: [],
};

OrderLine.contextTypes = {
  callbackForCanvasDraw: PropTypes.func,
};

export default OrderLine;
